/* eslint-disable no-param-reassign */
/*
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2025 Pearson Education, Inc.
 * All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Pearson Education, Inc. The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

/**
 * Aggregate Assignment MCQ Service
 * Simple service to fetch aggregate data and combine for VR - no store updates
 * @file AggregateAssignmentMCQService.js
 * @author Satyaki Bose
 */

import Framework, { BaseService } from '@greenville/framework';
import { AET_STORE_EVENTS } from '../common/Constants';

export default class AggregateAssignmentMCQService extends BaseService {
  constructor() {
    super(AET_STORE_EVENTS.AGGREGATE_ASSIGNMENT_MCQ_REQUESTED, 'aggregateAssignmentMCQStatus');
  }

  /**
   * API call for aggregate assignments, MCQs, and status
   * @param {*} eventName
   * @param {*} eventData - { courseLevelIsAET: boolean, courseId: string, bookId: string, userId: string }
   * @returns {Promise}
   */
  handleEvent(eventName, eventData = {}) {
    const {
      courseLevelIsAET, courseId, bookId, userId,
      clientVersion = 'V2', includeUnassigned = false, queryParams = ''
    } = eventData;
    if (!userId) {
      return Promise.reject(new Error('userId is required'));
    }
    const client = `marin${clientVersion}`;

    let url;
    if (courseLevelIsAET && courseId) {
      url = `/aet/users/${userId}/courses/${courseId}/mcqs?includeUnassigned=${!!includeUnassigned}${queryParams}`;
    } else if (bookId) {
      url = `/aet/books/${bookId}/users/${userId}/mcqs`;
    } else {
      return Promise.reject(new Error('Either courseId (when courseLevelIsAET is true) or bookId is required'));
    }

    return this.getClient(client).get(url);
  }

  // eslint-disable-next-line class-methods-use-this
  handleResponse(response, eventData = {}) {
    const { data } = response;
    const { courseLevelIsAET, includeUnassigned } = eventData;
    const config = { assignmentsData: null, mcqsData: null };

    if (!data) {
      Framework.getEventManager().publish(
        AET_STORE_EVENTS.AGGREGATE_ASSIGNMENT_MCQ_COMPLETED,
        config
      );
      return;
    }

    if (courseLevelIsAET) {
      // Remove redundant fields from assignmentsData
      const { unassignedQuestions, ...assignmentsOnly } = data;
      config.assignmentsData = assignmentsOnly;
      if (includeUnassigned && unassignedQuestions) {
        // Remove redundant fields from mcqsData
        // eslint-disable-next-line no-shadow
        const { assignments, unassignedQuestions, ...metadata } = data;
        config.mcqsData = {
          ...metadata,
          studentMCQData: unassignedQuestions
        };
      }
    } else {
      config.mcqsData = data;
    }

    Framework.getEventManager().publish(
      AET_STORE_EVENTS.AGGREGATE_ASSIGNMENT_MCQ_COMPLETED,
      config
    );
  }

  // eslint-disable-next-line class-methods-use-this
  handleError(error) {
    Framework.getEventManager().publish(AET_STORE_EVENTS.AGGREGATE_ASSIGNMENT_MCQ_FAILED, error);
  }
}
